import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import {
  FormBuilder,
  FieldRegistryImpl,
  type FormBuilderHandle,
  type FormSchema,
} from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { InterviewActivityStepProps } from '../types';
import { useInterviewStageManager } from '@/pages/ats-job-creation/tabs/interview-plan/stage/stage-manager';
import { useInterviewActivityDetailsSchema } from './useInterviewActivityDetailsSchema';
import { VariableInputAdapter } from './VariableInputAdapter';

const detailsFieldRegistry = new FieldRegistryImpl().register(
  'variableInput',
  VariableInputAdapter
);

export const InterviewActivityDetails = memo(
  forwardRef<StepFormApi, InterviewActivityStepProps>(
    ({ payloadRef, prefillVersion = 0, selectedActivity }, ref) => {
      const { activityAction } = useInterviewStageManager();
      const { stage = {} } = activityAction as { stage: { name?: string } };

      const participantDefaultOptions = useMemo(() => {
        const raw = (
          payloadRef.current.details as Record<string, unknown> | undefined
        )?.participant_default_options;
        return Array.isArray(raw)
          ? (raw as Array<{
              value: string;
              label: string;
              description?: string;
              image?: string;
            }>)
          : [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion]);

      const {
        schema,
        defaultValues: schemaDefaults,
        defaultLocation,
      } = useInterviewActivityDetailsSchema({
        stageName: stage?.name,
        selectedActivity,
        participantDefaultOptions,
      });

      const defaultValues = useMemo(() => {
        const rawDetails = (payloadRef.current.details ?? {}) as Record<
          string,
          unknown
        >;
        const { participant_default_options: _omit, ...detailsForForm } =
          rawDetails;
        return {
          ...schemaDefaults,
          ...detailsForForm,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [schemaDefaults, prefillVersion]);

      const formRef = useRef<FormBuilderHandle>(null);

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            if (!formRef.current) return true;
            return formRef.current.trigger();
          },
          syncToPayload: () => {
            if (!formRef.current) return;
            const values = formRef.current.getValues();
            const keep = (
              payloadRef.current.details as Record<string, unknown> | undefined
            )?.participant_default_options;
            payloadRef.current.details = {
              ...values,
              ...(Array.isArray(keep) && keep.length > 0
                ? { participant_default_options: keep }
                : {}),
            };
          },
        }),
        [payloadRef]
      );

      // Remount form when defaults become ready (location API) or edit data loads
      const formKey = `${prefillVersion}-${defaultLocation ?? 'loading'}`;

      return (
        <Flex
          data-interview-details-step
          justifyContent="center"
          w="100%"
          minH={0}
          flex="1"
          overflowY="auto"
          key={formKey}
          mb="12px"
        >
          <Flex w="560px" maxW="100%" flexDir="column">
            <FormBuilder
              ref={formRef}
              schema={schema as FormSchema}
              defaultValues={defaultValues}
              columns={1}
              mode="onBlur"
              fieldRegistry={detailsFieldRegistry}
            />
          </Flex>
        </Flex>
      );
    }
  )
);

InterviewActivityDetails.displayName = 'InterviewActivityDetails';
